import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const { data } = await api.get('/admin/dashboard');
      setStats(data);
    } catch (err) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><p className="text-gray-400">Loading dashboard...</p></div>;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">📊 Admin Dashboard</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard icon="📋" label="Bookings This Month" value={stats?.totalBookingsThisMonth || 0} color="blue" />
        <StatCard icon="💰" label="Advance Collected" value={`₹${(stats?.totalAdvanceCollected || 0).toLocaleString('en-IN')}`} color="green" />
        <StatCard icon="🧾" label="Total Revenue" value={`₹${(stats?.totalRevenue || 0).toLocaleString('en-IN')}`} color="indigo" />
        <StatCard icon="⏳" label="Pending Balance" value={`₹${(stats?.totalPendingBalance || 0).toLocaleString('en-IN')}`} color="orange" />
        <StatCard icon="👥" label="Active Agents" value={stats?.activeAgents || 0} color="purple" />
      </div>

      {/* Profit Margin Card */}
      <div className="mb-8 cursor-pointer" onClick={() => navigate('/admin/profit-margins')}>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl mb-1">📈</p>
              <p className="text-2xl font-bold text-gray-900">₹{(stats?.totalProfitMargin || 0).toLocaleString('en-IN')}</p>
              <p className="text-sm text-gray-500 mt-1">Total Profit Margin</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Total B2B Cost</p>
              <p className="text-lg font-semibold text-gray-700">₹{(stats?.totalB2BCost || 0).toLocaleString('en-IN')}</p>
            </div>
            <div className="text-gray-400 text-xl">→</div>
          </div>
        </div>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-1">Total Bookings (All Time)</h3>
          <p className="text-2xl font-bold text-gray-900">{stats?.totalBookings || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-1">Most Popular Trek</h3>
          <p className="text-lg font-bold text-gray-900">{stats?.popularTrek || 'N/A'}</p>
        </div>
      </div>

      {/* Upcoming Bookings */}
      <div className="bg-white rounded-xl shadow-sm border p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">🗓️ Upcoming Bookings</h2>
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {stats?.upcomingBookings?.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No upcoming bookings</p>
          ) : (
            stats?.upcomingBookings?.map(b => (
              <div key={b.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 border border-gray-50">
                <div>
                  <p className="text-sm font-medium text-gray-900">{b.customerName} <span className="text-gray-400 font-normal">· {b.trekName}</span></p>
                  <p className="text-xs text-gray-500 mt-0.5">{b.startDate} to {b.endDate} · {b.groupSize} member{b.groupSize > 1 ? 's' : ''}</p>
                </div>
                <span className="text-xs text-gray-400">{b.id}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">📝 Recent Activity</h2>
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {stats?.recentActivity?.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No activity yet</p>
          ) : (
            stats?.recentActivity?.map(log => (
              <div key={log.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 border border-gray-50">
                <div className="text-xs text-gray-400 whitespace-nowrap mt-0.5">
                  {new Date(log.timestamp).toLocaleString()}
                </div>
                <div className="flex-1">
                  <p className="text-sm">
                    <strong className="text-primary-700">{log.agentName || log.agentId}</strong>
                    {' '}<span className="text-gray-600">{log.details}</span>
                  </p>
                  {log.bookingId && <p className="text-xs text-gray-400 mt-0.5">Booking: {log.bookingId}</p>}
                </div>
                <ActionBadge action={log.action} />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  const colors = {
    blue: 'bg-blue-50 border-blue-100',
    green: 'bg-green-50 border-green-100',
    indigo: 'bg-indigo-50 border-indigo-100',
    orange: 'bg-orange-50 border-orange-100',
    purple: 'bg-purple-50 border-purple-100'
  };
  return (
    <div className={`rounded-xl border p-5 ${colors[color]}`}>
      <p className="text-2xl mb-1">{icon}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500 mt-1">{label}</p>
    </div>
  );
}

function ActionBadge({ action }) {
  const styles = {
    created_booking: 'bg-green-100 text-green-700',
    edited_booking: 'bg-yellow-100 text-yellow-700',
    deleted_booking: 'bg-red-100 text-red-700',
    generated_document: 'bg-blue-100 text-blue-700',
    edited_document: 'bg-purple-100 text-purple-700'
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${styles[action] || 'bg-gray-100 text-gray-600'}`}>
      {action?.replace(/_/g, ' ')}
    </span>
  );
}
